<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import PatientProfilesAPI from 'dashboard/api/patientProfiles.js';

const props = defineProps({
  contactId: {
    type: [Number, String],
    default: undefined,
  },
  // Contato atual — usado pra mostrar o risco de TRIAGEM de quem não é cadastrado.
  contact: {
    type: Object,
    default: null,
  },
});

// Risco de triagem (contato não-cadastrado): o classificador salva a cor/score no
// custom_attributes do contato. Só é usado quando não há perfil clínico.
const triageRisk = computed(() => {
  const ca = props.contact?.custom_attributes || {};
  if (!ca.risk_level) return null;
  return { level: ca.risk_level, score: ca.risk_score };
});

// Motivo do risco (por que essa prioridade) — salvo pelo classificador.
const riskReason = computed(
  () => props.contact?.custom_attributes?.risk_reason || null
);

const profile = ref(null);
const loading = ref(false);
const error = ref(null);

// Rótulos da fase da jornada (alinhados ao Dashboard Clínico).
const phaseLabels = {
  pre_op: 'Pré-op',
  surgery: 'Cirurgia realizada',
  post_op_phase_1: 'Pós-op Fase 1',
  post_op_phase_2: 'Pós-op Fase 2',
  discharged: 'Alta',
};

// Prioridade clínica (risk_level): verde / amarelo / vermelho.
const riskLabels = {
  verde: '🟢 Verde',
  amarelo: '🟡 Amarelo',
  vermelho: '🔴 Vermelho',
};

const riskClasses = {
  verde: 'bg-n-teal-3 text-n-teal-11',
  amarelo: 'bg-n-amber-3 text-n-amber-11',
  vermelho: 'bg-n-ruby-3 text-n-ruby-11',
};

async function load() {
  if (!props.contactId) {
    profile.value = null;
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const { data } = await PatientProfilesAPI.list({
      contactId: props.contactId,
    });
    // Um perfil clínico por contato — pega o primeiro resultado.
    profile.value = (data?.data || [])[0] || null;
  } catch (e) {
    error.value = e?.response?.data?.message || e.message;
    profile.value = null;
  } finally {
    loading.value = false;
  }
}

watch(() => props.contactId, load);
onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="px-1 py-1 text-sm">
    <div v-if="loading" class="text-xs text-n-slate-11 py-1">Carregando…</div>

    <div v-else-if="error" class="text-xs text-n-ruby-11 py-1">
      Erro ao carregar dados clínicos.
    </div>

    <div v-else-if="!profile">
      <!-- Não-cadastrado, mas já triado pela IA: mostra o risco de triagem -->
      <div v-if="triageRisk" class="flex flex-col gap-2">
        <div class="text-[10px] uppercase tracking-wide text-n-slate-10">
          Triagem (não cadastrado)
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs text-n-slate-11">Prioridade</span>
          <span
            class="text-xs font-semibold px-2 py-0.5 rounded-md whitespace-nowrap"
            :class="
              riskClasses[triageRisk.level] || 'bg-n-slate-3 text-n-slate-11'
            "
          >
            {{ riskLabels[triageRisk.level] || '—' }}
          </span>
        </div>
        <div
          v-if="riskReason"
          class="text-[11px] text-n-slate-11 text-right leading-tight"
        >
          Motivo: {{ riskReason }}
        </div>
      </div>
      <div v-else class="text-xs text-n-slate-10 italic py-1 leading-relaxed">
        Paciente não cadastrado clinicamente
      </div>
    </div>

    <dl v-else class="flex flex-col gap-2">
      <!-- Status / Fase -->
      <div class="flex items-center justify-between gap-2">
        <dt class="text-xs text-n-slate-11">Status</dt>
        <dd class="text-xs font-medium text-n-slate-12 text-right">
          {{ phaseLabels[profile.journey_phase] || '—' }}
        </dd>
      </div>

      <!-- Prioridade / risco -->
      <div class="flex items-center justify-between gap-2">
        <dt class="text-xs text-n-slate-11">Prioridade</dt>
        <dd>
          <span
            class="text-xs font-semibold px-2 py-0.5 rounded-md whitespace-nowrap"
            :class="
              riskClasses[profile.risk_level] || 'bg-n-slate-3 text-n-slate-11'
            "
          >
            {{ riskLabels[profile.risk_level] || '—' }}
          </span>
        </dd>
      </div>
      <div
        v-if="riskReason"
        class="text-[11px] text-n-slate-11 text-right leading-tight -mt-1"
      >
        Motivo: {{ riskReason }}
      </div>

      <!-- Dia pós-op -->
      <div class="flex items-center justify-between gap-2">
        <dt class="text-xs text-n-slate-11">Dia pós-op</dt>
        <dd class="text-xs font-medium text-n-slate-12 text-right">
          {{
            profile.post_op_day !== null && profile.post_op_day !== undefined
              ? `D+${profile.post_op_day}`
              : '—'
          }}
        </dd>
      </div>

      <!-- Procedimento -->
      <div class="flex items-center justify-between gap-2">
        <dt class="text-xs text-n-slate-11">Procedimento</dt>
        <dd class="text-xs font-medium text-n-slate-12 text-right truncate">
          {{ profile.procedure?.name || '—' }}
        </dd>
      </div>

      <!-- Médico responsável -->
      <div class="flex items-center justify-between gap-2">
        <dt class="text-xs text-n-slate-11">Médico</dt>
        <dd class="text-xs font-medium text-n-slate-12 text-right truncate">
          {{ profile.physician?.name || '—' }}
        </dd>
      </div>

      <!-- Alertas abertos (só aparece se houver) -->
      <div
        v-if="profile.open_alerts_count > 0"
        class="flex items-center justify-between gap-2"
      >
        <dt class="text-xs text-n-slate-11">Alertas abertos</dt>
        <dd>
          <span
            class="text-xs font-bold px-2 py-0.5 rounded-md bg-n-ruby-9 text-white"
          >
            {{ profile.open_alerts_count }}
          </span>
        </dd>
      </div>
    </dl>
  </div>
</template>
